import {
  useListUsers,
  useAssignUsersToTeams,
} from '@trufla-technology/trufla-trudomain/dist/lib/truMarket';
import { ListUsers } from '@trufla-technology/trufla-trudomain/dist/lib/truMarket/types';
import { useToastContext } from '@trufla-technology/trumarket-react-design-system';
import { debounce, differenceWith } from 'lodash';
import { useEffect, useRef } from 'react';

export const useAssignUserToTeam = (
  usersAssigned: ListUsers,
  teamKey: string,
  getUsers: () => void,
  setUnassignedUsers: React.Dispatch<React.SetStateAction<ListUsers>>,
) => {
  const { data, loading, handleFilter, handleOffset, query, paging } =
    useListUsers({
      paginate: { offset: 0, limit: 25 },
    });

  const toaster = useToastContext();
  const AssignUsersToTeams = useAssignUsersToTeams();
  const debouncedSearch = useRef(
    debounce(async (searchValue: string) => {
      await handleFilter({
        fullName: searchValue.length > 0 ? searchValue : undefined,
      });
    }, 400),
  ).current;

  useEffect(() => {
    if (data && usersAssigned) {
      setUnassignedUsers((prevUnassignedUsers: ListUsers) => {
        const newUnassignedUsers = differenceWith(
          data,
          usersAssigned,
          (a: ListUsers[number], b: ListUsers[number]) => a.key === b.key,
        );

        return differenceWith(
          [...prevUnassignedUsers, ...newUnassignedUsers],
          usersAssigned,
          (a: ListUsers[number], b: ListUsers[number]) => a.key === b.key,
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersAssigned, data]);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleAssignUser = (key: string) => {
    AssignUsersToTeams({
      assigning: [{ teamKey: teamKey, userKey: key }],
    })
      .then(() => {
        toaster.success('Successfully added user to the team');
        getUsers();
      })
      .catch((err) => {
        const errorMessage = err?.response?.data?.error.replaceAll('body.', '');
        toaster.error(errorMessage || 'Failed to add a team');
      });
  };

  const loadMore = () =>
    handleOffset(query.paginate?.offset! + query.paginate?.limit!);

  return {
    loadMore,
    loading,
    handleAssignUser,
    debouncedSearch,
    paging,
  };
};
